import { Download, FileArchive, Loader2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Progress } from '@/components/ui/progress'
import { useTranslations } from '@/hooks/useTranslations'

import type { BillExportTemplate, CustomerExportOption } from './exportBillsCsv'
import { defaultExportTemplateForTab } from './exportBillsCsv'

export type ExportFormat = 'csv' | 'zip'
export type PdfMode = 'report' | 'receipts' | 'csv'

type ExportCsvDialogProps = {
  tab: string
  disabled?: boolean
  isExporting: boolean
  customers: CustomerExportOption[]
  onExport: (template: BillExportTemplate, customerId?: string) => void
  onExportPdf?: (template: BillExportTemplate, year: number, pdfMode: PdfMode, month?: number) => void
}

function useElapsedSeconds(running: boolean) {
  const [elapsed, setElapsed] = useState(0)
  const startRef = useRef(0)

  useEffect(() => {
    if (!running) { setElapsed(0); return }
    startRef.current = Date.now()
    setElapsed(0)
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000)
    return () => clearInterval(id)
  }, [running])

  return elapsed
}

function formatElapsed(s: number): string {
  const m = Math.floor(s / 60)
  const sec = s % 60
  return m > 0 ? `${m}:${String(sec).padStart(2, '0')}` : `${sec} วินาที`
}

const TEMPLATE_OPTIONS: { value: BillExportTemplate; labelKey: string }[] = [
  { value: 'buy_monthly', labelKey: 'export_template_buy_monthly' },
  { value: 'sell_retail_monthly', labelKey: 'export_template_sell_retail_monthly' },
  { value: 'sell_retail_customer_monthly', labelKey: 'export_template_sell_retail_customer_monthly' },
  { value: 'sell_wholesale_all_monthly', labelKey: 'export_template_sell_wholesale_all_monthly' },
  { value: 'sell_wholesale_monthly', labelKey: 'export_template_sell_wholesale_monthly' },
  { value: 'sell_all_monthly', labelKey: 'export_template_sell_all_monthly' },
]

function templatesForTab(tab: string): BillExportTemplate[] {
  if (tab === 'buy') return ['buy_monthly']
  return [
    'sell_all_monthly',
    'sell_retail_monthly',
    'sell_retail_customer_monthly',
    'sell_wholesale_all_monthly',
    'sell_wholesale_monthly',
  ]
}

function buildYearOptions(): number[] {
  const currentYear = new Date().getFullYear()
  const years: number[] = []
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(y)
  }
  return years
}

const MONTH_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'ทั้งปี' },
  { value: '1', label: 'มกราคม' },
  { value: '2', label: 'กุมภาพันธ์' },
  { value: '3', label: 'มีนาคม' },
  { value: '4', label: 'เมษายน' },
  { value: '5', label: 'พฤษภาคม' },
  { value: '6', label: 'มิถุนายน' },
  { value: '7', label: 'กรกฎาคม' },
  { value: '8', label: 'สิงหาคม' },
  { value: '9', label: 'กันยายน' },
  { value: '10', label: 'ตุลาคม' },
  { value: '11', label: 'พฤศจิกายน' },
  { value: '12', label: 'ธันวาคม' },
]

export function ExportCsvDialog({
  tab,
  disabled,
  isExporting,
  customers,
  onExport,
  onExportPdf,
}: ExportCsvDialogProps) {
  const t = useTranslations('admin.bill_management')
  const [open, setOpen] = useState(false)
  const [format, setFormat] = useState<ExportFormat>('csv')
  const [pdfMode, setPdfMode] = useState<PdfMode>('report')
  const [year, setYear] = useState<number>(new Date().getFullYear())
  const [selectedMonth, setSelectedMonth] = useState<string>('all')
  const [template, setTemplate] = useState<BillExportTemplate>(defaultExportTemplateForTab(tab))
  const [customerId, setCustomerId] = useState<string>('')

  const availableTemplates = templatesForTab(tab)
  const yearOptions = buildYearOptions()

  useEffect(() => {
    setTemplate(defaultExportTemplateForTab(tab))
    setCustomerId('')
  }, [tab])

  useEffect(() => {
    if (!availableTemplates.includes(template)) {
      setTemplate(availableTemplates[0] ?? 'buy_monthly')
    }
  }, [availableTemplates, template])

  const showCustomerSelect =
    format === 'csv' &&
    (template === 'sell_wholesale_monthly' || template === 'sell_retail_customer_monthly')
  const customerLabel =
    template === 'sell_wholesale_monthly'
      ? t('export_wholesale_customer_label')
      : t('export_retail_customer_label')

  const handleConfirm = () => {
    if (format === 'zip' && onExportPdf) {
      const monthNum = selectedMonth !== 'all' ? Number(selectedMonth) : undefined
      onExportPdf(template, year, pdfMode, monthNum)
    } else {
      onExport(template, showCustomerSelect ? customerId : undefined)
    }
  }

  const isPdfDisabled = format === 'csv'
    ? (showCustomerSelect && !customerId)
    : false

  const elapsed = useElapsedSeconds(isExporting)

  const prevExporting = useRef(false)
  useEffect(() => {
    if (prevExporting.current && !isExporting && open) {
      setOpen(false)
    }
    prevExporting.current = isExporting
  }, [isExporting, open])

  const handleOpenChange = useCallback((v: boolean) => {
    if (isExporting) return
    setOpen(v)
  }, [isExporting])

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-full rounded-lg sm:w-auto"
          disabled={disabled || isExporting}
        >
          <Download className="mr-2 h-4 w-4 shrink-0" aria-hidden />
          {isExporting ? t('btn_export_loading') : t('btn_export')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" onPointerDownOutside={isExporting ? (e) => e.preventDefault() : undefined} onEscapeKeyDown={isExporting ? (e) => e.preventDefault() : undefined}>
        <DialogHeader>
          <DialogTitle>{t('export_dialog_title')}</DialogTitle>
          <DialogDescription>{t('export_dialog_description')}</DialogDescription>
        </DialogHeader>

        {isExporting ? (
          <div className="flex flex-col items-center gap-5 py-8">
            <div className="relative flex h-16 w-16 items-center justify-center">
              <div className="absolute inset-0 animate-ping rounded-full bg-primary/20" />
              <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                <FileArchive className="h-7 w-7 text-primary animate-pulse" />
              </div>
            </div>

            <div className="w-full space-y-2 px-4">
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2 font-medium text-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('export_loading_title')}
                </span>
                <span className="tabular-nums text-muted-foreground">
                  {formatElapsed(elapsed)}
                </span>
              </div>
              <div className="overflow-hidden rounded-full">
                <Progress value={undefined} className="h-2" />
              </div>
              <p className="text-center text-xs text-muted-foreground">
                {t('export_loading_desc')}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4 py-2">
              {/* Format selection */}
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium">{t('export_format_label')}</span>
                <Select value={format} onValueChange={(v) => setFormat(v as ExportFormat)}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="csv">CSV</SelectItem>
                    <SelectItem value="zip">{t('export_format_zip')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* ZIP output type (report PDF / receipts PDF / csv) */}
              {format === 'zip' && (
                <div className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium">{t('export_pdf_mode_label')}</span>
                  <Select value={pdfMode} onValueChange={(v) => setPdfMode(v as PdfMode)}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="report">{t('export_pdf_mode_report')}</SelectItem>
                      <SelectItem value="receipts">{t('export_pdf_mode_receipts')}</SelectItem>
                      <SelectItem value="csv">{t('export_zip_mode_csv')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Year + Month selector for ZIP */}
              {format === 'zip' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">{t('export_year_label')}</span>
                    <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {yearOptions.map((y) => (
                          <SelectItem key={y} value={String(y)}>
                            {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-sm font-medium">{t('export_month_label')}</span>
                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTH_OPTIONS.map((m) => (
                          <SelectItem key={m.value} value={m.value}>
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Template selection */}
              <div className="flex flex-col gap-1.5">
                <span className="text-sm font-medium">{t('export_template_label')}</span>
                <Select
                  value={template}
                  onValueChange={(v) => setTemplate(v as BillExportTemplate)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TEMPLATE_OPTIONS.filter((opt) => availableTemplates.includes(opt.value)).map(
                      (opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {t(opt.labelKey)}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Customer select (CSV only, for specific templates) */}
              {showCustomerSelect ? (
                <div className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium">{customerLabel}</span>
                  <Select value={customerId} onValueChange={setCustomerId}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('export_wholesale_customer_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                          {c.phone_number ? ` (${c.phone_number})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}

              {/* ZIP hint */}
              {format === 'zip' && (
                <p className="text-xs text-muted-foreground">
                  {t('export_pdf_hint')}
                </p>
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                {t('export_dialog_cancel')}
              </Button>
              <Button
                type="button"
                disabled={isExporting || isPdfDisabled}
                onClick={handleConfirm}
              >
                {t('export_dialog_confirm')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
